import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  Opportunity,
  OpportunityStage,
  OpportunityStatus,
  SortDirection,
} from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE, OPPORTUNITY_STAGES, OPPORTUNITY_STATUSES } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate, formatDateTime } from '../../utils/date.js';
import { useBackNavigation } from '../../utils/navigation.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { MemberMultiFilter } from '../members/MemberMultiFilter.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedString, savedStringArray, useFilterDefaults } from '../filters/useFilterDefaults.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { useOpportunities, useOpportunity, useUpdateOpportunity } from './api.js';
import { OpportunityForecastPanel } from './OpportunityForecastPanel.js';
import { OpportunityFormModal } from './OpportunityFormModal.js';
import { useTrackRecent } from '../quick-access/useTrackRecent.js';
import {
  OPPORTUNITY_STAGE_LABELS,
  OPPORTUNITY_STAGE_TONES,
  OPPORTUNITY_STATUS_LABELS,
  OPPORTUNITY_STATUS_TONES,
} from './status.js';

/** Column keys the opportunities list can be sorted by (mirrors the backend sort allow-list). */
type OpportunitySortKey =
  | 'name'
  | 'customerName'
  | 'salesRepName'
  | 'stage'
  | 'status'
  | 'closeDate'
  | 'serviceCategoryName'
  | 'summary'
  | 'createdAt'
  | 'updatedAt';

type OpportunitySort = { key: OpportunitySortKey; dir: SortDirection };

/** Active close-date quick filter: a week/month window or "overdue" (closed before today). */
type CloseQuickFilter =
  | { mode: 'week' | 'month'; from: string; to: string }
  | { mode: 'overdue' }
  | null;

const currencyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** A clickable table header that toggles server-side sorting for its column. */
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
}: {
  label: string;
  sortKey: OpportunitySortKey;
  sort: OpportunitySort;
  onToggle: (key: OpportunitySortKey) => void;
}): JSX.Element {
  const active = sort.key === sortKey;
  const indicator = active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          active ? 'text-slate-700' : 'hover:text-slate-700'
        }`}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span className={`text-[10px] ${active ? 'text-slate-700' : 'text-slate-300'}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
}

export function OpportunitiesPage(): JSX.Element {
  const { can, canSeeMenu } = useAuth();
  const canEdit = can('sales', 'edit');
  const backNav = useBackNavigation();
  const navigate = useNavigate();
  const rowLink = useRowLink();

  const [page, setPage] = useState(1);
  const [stage, setStage] = useState<OpportunityStage | ''>('');
  const [status, setStatus] = useState<OpportunityStatus | ''>('');
  const [customerId, setCustomerId] = useState('');
  const [salesRepIds, setSalesRepIds] = useState<string[]>([]);
  const [serviceCategoryId, setServiceCategoryId] = useState('');
  const [closeQuick, setCloseQuick] = useState<CloseQuickFilter>(null);
  const [sort, setSort] = useState<OpportunitySort>({ key: 'createdAt', dir: 'desc' });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Opportunity | null>(null);
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null);

  useTrackRecent(
    formOpen && editing
      ? {
          entity: 'opportunity',
          id: editing.id,
          label: editing.name,
          path: `/sales/opportunities/${editing.id}`,
        }
      : null,
  );
  const { id: routeOpportunityId } = useParams<{ id?: string }>();

  // Deep-link (e.g. from the dashboard): ?open=<id> opens that opportunity's modal.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setPendingOpenId(openId);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Route-based deep-link: /sales/opportunities/:id opens that opportunity's standard modal.
  useEffect(() => {
    if (routeOpportunityId) {
      setPendingOpenId(routeOpportunityId);
    }
  }, [routeOpportunityId]);

  // Drill-down from the forecast panel: fetch the opportunity, then open its modal.
  const pendingQuery = useOpportunity(pendingOpenId);
  useEffect(() => {
    if (pendingOpenId && pendingQuery.data) {
      setEditing(pendingQuery.data);
      setFormOpen(true);
      setPendingOpenId(null);
    }
  }, [pendingOpenId, pendingQuery.data]);

  const closeDateFrom = closeQuick && closeQuick.mode !== 'overdue' ? closeQuick.from : undefined;
  const closeDateTo =
    closeQuick == null
      ? undefined
      : closeQuick.mode === 'overdue'
        ? addDaysIso(todayIso(), -1)
        : closeQuick.to;

  const query = useOpportunities({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    stage: stage || undefined,
    status: status || undefined,
    customerId: customerId || undefined,
    salesRepIds: salesRepIds.length > 0 ? salesRepIds : undefined,
    serviceCategoryId: serviceCategoryId || undefined,
    closeDateFrom,
    closeDateTo,
    sortBy: sort.key,
    sortDir: sort.dir,
  });

  const filterDefaults = useFilterDefaults('opportunities', (saved) => {
    const savedStage = savedString(saved, 'stage');
    if (savedStage !== null) setStage(savedStage as OpportunityStage | '');
    const savedStatus = savedString(saved, 'status');
    if (savedStatus !== null) setStatus(savedStatus as OpportunityStatus | '');
    const savedCustomer = savedString(saved, 'customerId');
    if (savedCustomer !== null) setCustomerId(savedCustomer);
    const savedReps = savedStringArray(saved, 'salesRepIds');
    if (savedReps !== null) setSalesRepIds(savedReps);
    const savedCategory = savedString(saved, 'serviceCategoryId');
    if (savedCategory !== null) setServiceCategoryId(savedCategory);
  });

  const updateOpportunity = useUpdateOpportunity();

  // Drag a forecast row's grip to a month → set its close date to that month's last day.
  const handleMoveCloseMonth = (opportunityId: string, month: string): void => {
    const y = Number(month.slice(0, 4));
    const m = Number(month.slice(5, 7));
    const lastDay = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    updateOpportunity.mutate({ id: opportunityId, input: { closeDate: lastDay } });
  };

  // Mark an opportunity Lost - Project Cancelled straight from the forecast drill-down.
  const handleMarkLost = (opportunityId: string): void => {
    updateOpportunity.mutate({
      id: opportunityId,
      input: { status: 'lost_project_cancelled' },
    });
  };

  const serviceCategoriesQuery = useServiceCategoryOptions();
  const serviceCategories = serviceCategoriesQuery.data ?? [];

  // Close-date quick filter: chevrons in another mode snap to the current period, then shift.
  const snapWeek = (): void => {
    setPage(1);
    setCloseQuick({ mode: 'week', ...currentWeekRange() });
  };
  const shiftWeek = (delta: number): void => {
    setPage(1);
    setCloseQuick((prev) => {
      const base = prev && prev.mode === 'week' ? { from: prev.from, to: prev.to } : currentWeekRange();
      return {
        mode: 'week',
        from: addDaysIso(base.from, delta * 7),
        to: addDaysIso(base.to, delta * 7),
      };
    });
  };
  const snapMonth = (): void => {
    setPage(1);
    setCloseQuick({ mode: 'month', ...monthRangeOf(todayIso()) });
  };
  const shiftCloseMonth = (delta: number): void => {
    setPage(1);
    setCloseQuick((prev) => {
      const baseFrom = prev && prev.mode === 'month' ? prev.from : monthRangeOf(todayIso()).from;
      return { mode: 'month', ...shiftMonthRange(baseFrom, delta) };
    });
  };
  const toggleOverdue = (): void => {
    setPage(1);
    setCloseQuick((prev) => (prev?.mode === 'overdue' ? null : { mode: 'overdue' }));
  };

  const closeQuickLabel =
    closeQuick == null
      ? null
      : closeQuick.mode === 'overdue'
        ? 'Close date before today'
        : `${formatDate(closeQuick.from)} – ${formatDate(closeQuick.to)}`;

  const toggleSort = (key: OpportunitySortKey): void => {
    setPage(1);
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const opportunities = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  // Closing returns the address bar to the list when the modal was opened via a deep link.
  const closeForm = (): void => {
    setFormOpen(false);
    setEditing(null);
    if (routeOpportunityId) navigate('/sales/opportunities');
  };

  const hasFilters = Boolean(
    stage || status || customerId || salesRepIds.length > 0 || serviceCategoryId || closeQuick,
  );

  return (
    <div>
      <PageHeader
        title="Opportunities"
        description="Sales pipeline: deals tracked from lead through close."
        actions={
          <>
            {backNav.canGoBack ? (
              <Button variant="secondary" onClick={backNav.goBack}>
                ← {backNav.backLabel}
              </Button>
            ) : null}
            {canEdit ? (
              <Button onClick={openCreate}>
                <Icon name="agreements" className="h-4 w-4" />
                New opportunity
              </Button>
            ) : null}
          </>
        }
      />

      {canSeeMenu('forecast') ? (
        <OpportunityForecastPanel
          onOpenOpportunity={(id) => setPendingOpenId(id)}
          onMoveCloseMonth={canEdit ? handleMoveCloseMonth : undefined}
          onMarkLost={canEdit ? handleMarkLost : undefined}
          filters={{
            customerId: customerId || undefined,
            salesRepIds: salesRepIds.length > 0 ? salesRepIds : undefined,
            serviceCategoryId: serviceCategoryId || undefined,
            stage: stage || undefined,
            status: status || undefined,
          }}
        />
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <div className="w-64">
            <CustomerSelectField
              value={customerId}
              onChange={(id) => {
                setPage(1);
                setCustomerId(id);
              }}
              allowClear
              placeholder="All companies"
            />
          </div>
          <MemberMultiFilter
            selected={salesRepIds}
            onChange={(next) => {
              setPage(1);
              setSalesRepIds(next);
            }}
            allLabel="All sales reps"
          />
          <select
            value={stage}
            onChange={(e) => {
              setPage(1);
              setStage(e.target.value as OpportunityStage | '');
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All stages</option>
            {OPPORTUNITY_STAGES.map((value) => (
              <option key={value} value={value}>
                {OPPORTUNITY_STAGE_LABELS[value]}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value as OpportunityStatus | '');
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All statuses</option>
            {OPPORTUNITY_STATUSES.map((value) => (
              <option key={value} value={value}>
                {OPPORTUNITY_STATUS_LABELS[value]}
              </option>
            ))}
          </select>
          <select
            value={serviceCategoryId}
            onChange={(e) => {
              setPage(1);
              setServiceCategoryId(e.target.value);
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All types</option>
            {serviceCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded-lg border border-slate-300 text-sm">
              <button
                type="button"
                onClick={() => shiftWeek(-1)}
                className="px-2 py-2 hover:bg-slate-50"
                aria-label="Previous week"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={snapWeek}
                className={`border-x border-slate-300 px-3 py-2 ${
                  closeQuick?.mode === 'week'
                    ? 'bg-brand-50 font-medium text-brand-700'
                    : 'hover:bg-slate-50'
                }`}
              >
                Week
              </button>
              <button
                type="button"
                onClick={() => shiftWeek(1)}
                className="px-2 py-2 hover:bg-slate-50"
                aria-label="Next week"
              >
                ›
              </button>
            </div>
            <div className="flex overflow-hidden rounded-lg border border-slate-300 text-sm">
              <button
                type="button"
                onClick={() => shiftCloseMonth(-1)}
                className="px-2 py-2 hover:bg-slate-50"
                aria-label="Previous month"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={snapMonth}
                className={`border-x border-slate-300 px-3 py-2 ${
                  closeQuick?.mode === 'month'
                    ? 'bg-brand-50 font-medium text-brand-700'
                    : 'hover:bg-slate-50'
                }`}
              >
                Month
              </button>
              <button
                type="button"
                onClick={() => shiftCloseMonth(1)}
                className="px-2 py-2 hover:bg-slate-50"
                aria-label="Next month"
              >
                ›
              </button>
            </div>
            <button
              type="button"
              onClick={toggleOverdue}
              className={`rounded-lg border px-3 py-2 text-sm ${
                closeQuick?.mode === 'overdue'
                  ? 'border-rose-300 bg-rose-50 font-medium text-rose-700'
                  : 'border-slate-300 hover:bg-slate-50'
              }`}
            >
              Overdue
            </button>
            {closeQuick ? (
              <>
                <span className="text-xs text-slate-500">{closeQuickLabel}</span>
                <button
                  type="button"
                  onClick={() => {
                    setPage(1);
                    setCloseQuick(null);
                  }}
                  className="text-xs text-slate-500 underline hover:text-slate-700"
                >
                  All dates
                </button>
              </>
            ) : null}
          </div>
          <FilterDefaultsButtons
            handle={filterDefaults}
            onSave={() =>
              filterDefaults.save({ stage, status, customerId, salesRepIds, serviceCategoryId })
            }
          />
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <SortableHeader label="Name" sortKey="name" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Company"
                sortKey="customerName"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Sales rep"
                sortKey="salesRepName"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Type"
                sortKey="serviceCategoryName"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader label="Stage" sortKey="stage" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Close date"
                sortKey="closeDate"
                sort={sort}
                onToggle={toggleSort}
              />
              <th className="px-4 py-3 text-right font-medium">One-Time Total</th>
              <th className="px-4 py-3 text-right font-medium">Recurring Total</th>
              <th className="px-4 py-3 text-right font-medium">Total</th>
              <SortableHeader label="Summary" sortKey="summary" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Created"
                sortKey="createdAt"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Updated"
                sortKey="updatedAt"
                sort={sort}
                onToggle={toggleSort}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={13}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={opportunities.length === 0}
              emptyTitle="No opportunities found"
              emptyDescription={
                hasFilters
                  ? 'Try a different filter.'
                  : 'Create your first opportunity to get started.'
              }
              emptyAction={
                canEdit && !hasFilters ? (
                  <Button onClick={openCreate}>New opportunity</Button>
                ) : undefined
              }
            >
              {opportunities.map((opportunity) => (
                <tr
                  key={opportunity.id}
                  className="cursor-pointer hover:bg-slate-50"
                  {...rowLink(`/sales/opportunities/${opportunity.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{opportunity.name}</td>
                  <td className="px-4 py-3 text-slate-600">{opportunity.customerName || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{opportunity.salesRepName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {opportunity.serviceCategoryName ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={OPPORTUNITY_STAGE_TONES[opportunity.stage]}>
                      {OPPORTUNITY_STAGE_LABELS[opportunity.stage]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={OPPORTUNITY_STATUS_TONES[opportunity.status]}>
                      {OPPORTUNITY_STATUS_LABELS[opportunity.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(opportunity.closeDate)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {currencyFormat.format(Number(opportunity.oneTimeTotal))}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {currencyFormat.format(Number(opportunity.recurringTotal))}
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums text-slate-900">
                    {currencyFormat.format(Number(opportunity.total))}
                  </td>
                  <td
                    className="max-w-xs truncate px-4 py-3 text-slate-600"
                    title={opportunity.summary ?? undefined}
                  >
                    {opportunity.summary ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateTime(opportunity.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateTime(opportunity.updatedAt)}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && opportunities.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <OpportunityFormModal isOpen={formOpen} opportunity={editing} onClose={closeForm} />
    </div>
  );
}

/** Today's date as local `yyyy-mm-dd`. */
function todayIso(): string {
  return toIsoDate(new Date());
}

/** Formats a local Date as `yyyy-mm-dd`. */
function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

/** Moves a `yyyy-mm-dd` date by `days` (local calendar). */
function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return toIsoDate(new Date(y ?? 0, (m ?? 1) - 1, (d ?? 1) + days));
}

/** The current Monday–Sunday week as an inclusive `yyyy-mm-dd` range. */
function currentWeekRange(): { from: string; to: string } {
  const now = new Date();
  const mondayOffset = (now.getDay() + 6) % 7;
  const from = toIsoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() - mondayOffset));
  return { from, to: addDaysIso(from, 6) };
}

/** The full calendar month containing `iso`, as an inclusive `yyyy-mm-dd` range. */
function monthRangeOf(iso: string): { from: string; to: string } {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  const prefix = `${year}-${String(month).padStart(2, '0')}`;
  const lastDay = new Date(year, month, 0).getDate();
  return { from: `${prefix}-01`, to: `${prefix}-${String(lastDay).padStart(2, '0')}` };
}

/** Shifts the month containing `fromIso` by `delta` months and returns that month's range. */
function shiftMonthRange(fromIso: string, delta: number): { from: string; to: string } {
  const year = Number(fromIso.slice(0, 4));
  const month = Number(fromIso.slice(5, 7));
  return monthRangeOf(toIsoDate(new Date(year, month - 1 + delta, 1)));
}
